import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, Copy, Check, Linkedin, Instagram, Hash, Sparkles, ChevronDown, ChevronUp, Twitter, Image as ImageIcon, Download, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Skeleton } from "../components/ui/skeleton";

import ContentCard from "../components/content/ContentCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hookLabels = {
  curiosity: { label: "Curiosity", color: "bg-blue-100 text-blue-700" },
  contrarian: { label: "Contrarian", color: "bg-purple-100 text-purple-700" },
  fear_of_loss: { label: "FOMO", color: "bg-red-100 text-red-700" },
  authority: { label: "Authority", color: "bg-emerald-100 text-emerald-700" },
  practical_benefit: { label: "Practical", color: "bg-amber-100 text-amber-700" },
};

export default function ContentStudio() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [generatingImages, setGeneratingImages] = useState(new Set());

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content?region=${region}&brand=${brand}`);
      setContent(res.data);
    } catch (error) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => {
    setLoading(true);
    fetchContent();
  }, [fetchContent, brand]);

  const copy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggle = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  const generateImage = async (contentId) => {
    setGeneratingImages(prev => new Set([...prev, contentId]));
    try {
      await axios.post(`${API}/content/${contentId}/generate-image`);
      toast.success("Image generation started!");
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/content/${contentId}`);
          if (res.data.image_status === "completed" || res.data.image_status === "failed") {
            clearInterval(pollInterval);
            setGeneratingImages(prev => {
              const next = new Set(prev);
              next.delete(contentId);
              return next;
            });
            fetchContent();
            if (res.data.image_status === "completed") {
              toast.success("Image generated!");
            } else {
              toast.error("Image generation failed");
            }
          }
        } catch (e) {
          console.debug("Image gen poll failed:", e?.message || e);
        }
      }, 5000);
    } catch (error) {
      setGeneratingImages(prev => {
        const next = new Set(prev);
        next.delete(contentId);
        return next;
      });
      toast.error("Failed to start image generation");
    }
  };

  const exportPost = async (contentId, platform = 'all') => {
    try {
      toast.loading("Preparing export...", { id: `export-${contentId}` });
      const response = await axios.get(`${API}/content/${contentId}/export?platform=${platform}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `blackswan_export_${contentId.slice(0,8)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export ready!", { id: `export-${contentId}` });
    } catch (error) {
      toast.error("Export failed", { id: `export-${contentId}` });
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="content-loading">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-40 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-48 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
        </div>
        {/* Content Cards Skeleton */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div 
              key={i} 
              className={`rounded-xl overflow-hidden animate-fadeIn stagger-${i} ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
            >
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className={`h-5 w-20 rounded-full ${darkMode ? 'bg-[#21262d]' : ''}`} />
                  <Skeleton className={`h-4 w-24 ${darkMode ? 'bg-[#21262d]' : ''}`} />
                </div>
                <Skeleton className={`h-6 w-3/4 ${darkMode ? 'bg-[#21262d]' : ''}`} />
              </div>
              <div className="p-5 space-y-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                  <Skeleton className={`h-4 w-24 mb-3 ${darkMode ? 'bg-[#30363d]' : ''}`} />
                  <Skeleton className={`h-4 w-full mb-2 ${darkMode ? 'bg-[#30363d]' : ''}`} />
                  <Skeleton className={`h-4 w-4/5 ${darkMode ? 'bg-[#30363d]' : ''}`} />
                </div>
                <Skeleton className={`h-12 w-full rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="content-studio-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Content Studio</h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{content.length} posts ready to publish</p>
        </div>
      </div>

      {content.length === 0 ? (
        <div className={`rounded-xl p-16 text-center ${darkMode ? 'bg-[#161b22] border border-[#30363d] text-gray-400' : 'bg-white border border-gray-100 shadow-sm text-gray-500'}`}>
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No content yet</p>
          <p className="text-sm mt-1">Run an intelligence scan to generate content</p>
        </div>
      ) : (
        <div className="space-y-4">
          {content.map((item, i) => (
            <ContentCard
              key={item.id}
              item={item}
              index={i}
              darkMode={darkMode}
              copiedId={copiedId}
              copy={copy}
              expanded={expanded}
              toggle={toggle}
              generatingImages={generatingImages}
              generateImage={generateImage}
              exportPost={exportPost}
              hookLabels={hookLabels}
            />
          ))}
        </div>
      )}
    </div>
  );
}
